import { useEffect, useState, useRef } from "react";
import { API } from "../lib/api";

/**
 * Full-screen overlay shown when the backend stops responding (502/503/network
 * error) — typically during a redeploy. Polls the backend's lightweight
 * health-check endpoint every 4 seconds and dismisses itself when service
 * comes back. The user is offered a "recargar" button so the browser pulls
 * the freshly-deployed frontend bundle.
 */
export default function SystemUpdateOverlay() {
  const [visible, setVisible] = useState(false);
  const [recovered, setRecovered] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const consecutiveFailsRef = useRef(0);
  const tickRef = useRef(null);
  const pollRef = useRef(null);

  // Lightweight health ping. We deliberately ignore auth so it works during
  // boot too.
  const ping = async () => {
    try {
      const resp = await fetch(`${API}/`, {
        method: "GET",
        cache: "no-store",
        // 6s timeout via AbortController
        signal: AbortSignal.timeout(6000),
      });
      if (resp.ok) {
        consecutiveFailsRef.current = 0;
        if (visible) {
          // Backend just recovered — switch to "ready" state.
          setRecovered(true);
        }
        return true;
      }
      // 502/503/504 → treat as down
      if (resp.status >= 500) {
        consecutiveFailsRef.current += 1;
      }
    } catch (e) {
      // Network/abort/timeout
      consecutiveFailsRef.current += 1;
    }
    if (consecutiveFailsRef.current >= 2 && !visible) {
      setVisible(true);
      setRecovered(false);
      setSeconds(0);
    }
    return false;
  };

  // Slow background poller — runs every 15s normally.
  // When overlay is visible, poll faster (every 4s).
  useEffect(() => {
    const startPoll = (intervalMs) => {
      if (pollRef.current) clearInterval(pollRef.current);
      pollRef.current = setInterval(ping, intervalMs);
    };
    startPoll(visible ? 4000 : 15000);
    return () => pollRef.current && clearInterval(pollRef.current);
  }, [visible]); // eslint-disable-line react-hooks/exhaustive-deps

  // Counter while overlay is open
  useEffect(() => {
    if (!visible || recovered) {
      if (tickRef.current) clearInterval(tickRef.current);
      tickRef.current = null;
      return;
    }
    tickRef.current = setInterval(() => setSeconds((s) => s + 1), 1000);
    return () => tickRef.current && clearInterval(tickRef.current);
  }, [visible, recovered]);

  // Listen for global "backend-error" events fired from the axios interceptor
  // (instant detection without waiting for the slow poll).
  useEffect(() => {
    const onBackendError = () => {
      consecutiveFailsRef.current = Math.max(consecutiveFailsRef.current, 2);
      ping(); // immediate verification
    };
    window.addEventListener("backend-down", onBackendError);
    return () => window.removeEventListener("backend-down", onBackendError);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (!visible) return null;

  return (
    <div
      data-testid="system-update-overlay"
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 9999,
        background: "rgba(8, 10, 18, 0.94)",
        backdropFilter: "blur(8px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 24,
      }}
    >
      <div
        style={{
          maxWidth: 480,
          width: "100%",
          background: "#0f1320",
          border: "1px solid rgba(255,255,255,0.08)",
          borderRadius: 16,
          padding: "32px 28px",
          color: "#fff",
          textAlign: "center",
          boxShadow: "0 20px 60px rgba(0,0,0,0.55)",
        }}
      >
        {!recovered ? (
          <>
            <div style={{ marginBottom: 18, display: "flex", justifyContent: "center" }}>
              <Spinner />
            </div>
            <h2 style={{ fontSize: 22, fontWeight: 800, marginBottom: 8, letterSpacing: "-0.01em" }}>
              Actualizando sistema…
            </h2>
            <p style={{ color: "rgba(255,255,255,0.65)", lineHeight: 1.5, marginBottom: 18, fontSize: 14 }}>
              Estamos aplicando una actualización en el servidor. El dashboard volverá automáticamente en cuanto termine. No cierres la ventana.
            </p>
            <div
              data-testid="system-update-counter"
              style={{
                display: "inline-block",
                padding: "8px 16px",
                background: "rgba(255,255,255,0.06)",
                borderRadius: 999,
                fontSize: 13,
                fontVariantNumeric: "tabular-nums",
                color: "rgba(255,255,255,0.8)",
              }}
            >
              Tiempo transcurrido: <strong style={{ color: "#fff" }}>{formatTime(seconds)}</strong>
            </div>
            <p style={{ color: "rgba(255,255,255,0.35)", fontSize: 12, marginTop: 18 }}>
              Esto suele tomar entre 30 segundos y 2 minutos.
            </p>
          </>
        ) : (
          <>
            <div style={{ marginBottom: 18 }}>
              <CheckIcon />
            </div>
            <h2 style={{ fontSize: 22, fontWeight: 800, marginBottom: 8, letterSpacing: "-0.01em" }}>
              ¡Sistema listo!
            </h2>
            <p style={{ color: "rgba(255,255,255,0.65)", lineHeight: 1.5, marginBottom: 22, fontSize: 14 }}>
              La actualización terminó. Recarga la página para usar la nueva versión.
            </p>
            <button
              data-testid="system-update-reload"
              onClick={() => window.location.reload()}
              style={{
                background: "#fff",
                color: "#0f1320",
                fontWeight: 700,
                fontSize: 14,
                border: 0,
                borderRadius: 999,
                padding: "12px 28px",
                cursor: "pointer",
                letterSpacing: "0.01em",
              }}
            >
              Recargar ahora
            </button>
          </>
        )}
      </div>
    </div>
  );
}

function formatTime(s) {
  const m = Math.floor(s / 60);
  const r = s % 60;
  if (m === 0) return `${r}s`;
  return `${m}m ${String(r).padStart(2, "0")}s`;
}

function Spinner() {
  return (
    <div
      style={{
        width: 48,
        height: 48,
        border: "3px solid rgba(255,255,255,0.12)",
        borderTopColor: "#fff",
        borderRadius: "50%",
        animation: "sdmSpin 0.9s linear infinite",
      }}
    />
  );
}

function CheckIcon() {
  return (
    <div
      style={{
        width: 56,
        height: 56,
        margin: "0 auto",
        borderRadius: "50%",
        background: "rgba(34,197,94,0.16)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="20 6 9 17 4 12" />
      </svg>
    </div>
  );
}
